/**
 * Несохранённые правки: очередь на повтор, переживающая перезагрузку.
 *
 * Каждая правка уходит на сервер сразу — черновик живёт в базе, а не в
 * браузере, и закрытая вкладка ничего не теряет. Но между «нажал» и «ушло»
 * есть сеть, и она иногда моргает. Раньше такой случай заканчивался тостом
 * об ошибке: тост гаснет, значение откатывается к прежнему, и человек
 * узнаёт о потере только случайно.
 *
 * Теперь неудавшаяся правка попадает в очередь. Очередь:
 *  · лежит в localStorage — переживает перезагрузку и закрытие вкладки;
 *  · повторяется сама с нарастающими паузами;
 *  · видна человеку полосой «N правок не сохранились» с кнопкой «Повторить»,
 *    и полоса не гаснет, пока правки не уйдут или их не отменят.
 *
 * Это не замена серверному черновику, а страховка на последнюю милю.
 */
import type { BrandChannelPatch, ChannelCode, MutationResponse } from '@shared/types.ts'
import { api } from '../lib/api.js'

const STORAGE_KEY = 'bm.pendingEdits'
/** Паузы между попытками: 5 секунд, полминуты, две минуты, дальше по две. */
const BACKOFF_MS = [5_000, 30_000, 120_000]

export type PendingEdit =
  | { id: string; at: number; tries: number; label: string; kind: 'brand'; brandId: number; input: Record<string, unknown> }
  | { id: string; at: number; tries: number; label: string; kind: 'channel'; brandId: number; channel: ChannelCode; patch: BrandChannelPatch }
  | {
    id: string; at: number; tries: number; label: string; kind: 'categoryChannel'
    brandId: number; brandCategoryId: number; channel: ChannelCode
    input: { isActive?: boolean; tier?: string | null }
  }
  | { id: string; at: number; tries: number; label: string; kind: 'comment'; brandId: number; body: string; channel?: ChannelCode }

/** Та же правка, но без служебных полей: их проставляет очередь.
    Omit по объединению типов пришлось бы раздавать по каждому варианту —
    отсюда распределяющий условный тип. */
export type NewPendingEdit = PendingEdit extends infer T
  ? (T extends PendingEdit ? Omit<T, 'id' | 'at' | 'tries'> : never)
  : never

let queue: PendingEdit[] = load()
const listeners = new Set<() => void>()
let timer: number | null = null

function load(): PendingEdit[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? (parsed as PendingEdit[]) : []
  } catch {
    return []
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue))
  } catch {
    /* Переполненное хранилище не должно ронять правку — она и так в очереди. */
  }
}

function notify(): void {
  save()
  for (const listener of listeners) listener()
}

export function pendingEdits(): readonly PendingEdit[] {
  return queue
}

export function subscribePending(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

export function dropPending(id: string): void {
  queue = queue.filter((edit) => edit.id !== id)
  notify()
}

export function clearPending(): void {
  queue = []
  notify()
}

/** Поставить правку в очередь на повтор. */
export function queueEdit(edit: NewPendingEdit): void {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  queue = [...queue.filter((item) => item.id !== id), { ...edit, id, at: Date.now(), tries: 0 } as PendingEdit]
  notify()
  schedule()
}

function send(edit: PendingEdit): Promise<MutationResponse> {
  switch (edit.kind) {
    case 'brand': return api.updateBrand(edit.brandId, edit.input)
    case 'channel': return api.updateChannel(edit.brandId, edit.channel, edit.patch)
    case 'categoryChannel':
      return api.updateCategoryChannel(edit.brandId, edit.brandCategoryId, edit.channel, edit.input)
    case 'comment':
      return api.addComment(edit.brandId, { body: edit.body, channelCode: edit.channel ?? null })
  }
}

/**
 * Попытка отправить всё, что скопилось. Возвращает, сколько осталось.
 * Повтор идёт по очереди, а не пачкой: порядок правок сохраняется.
 */
export async function flushPending(): Promise<number> {
  for (const edit of [...queue]) {
    try {
      await send(edit)
      queue = queue.filter((item) => item.id !== edit.id)
      notify()
    } catch {
      edit.tries += 1
      notify()
      break
    }
  }
  if (queue.length > 0) schedule()
  return queue.length
}

function schedule(): void {
  if (timer !== null || queue.length === 0) return
  const tries = Math.min(...queue.map((edit) => edit.tries))
  const delay = BACKOFF_MS[Math.min(tries, BACKOFF_MS.length - 1)] ?? 120_000
  timer = window.setTimeout(() => {
    timer = null
    void flushPending()
  }, delay)
}

/* Вкладка вернулась в сеть или на экран — пробуем сразу, не дожидаясь паузы. */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { void flushPending() })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void flushPending()
  })
  /* Уходя со страницы с несохранёнными правками, человек должен об этом
     узнать: черновик на сервере, но именно эти правки до него не дошли. */
  window.addEventListener('beforeunload', (event) => {
    if (queue.length === 0) return
    event.preventDefault()
    event.returnValue = ''
  })
  if (queue.length > 0) schedule()
}
